<template>
  <div class="relative profile-menu">
    <button 
      @click="toggleProfileMenu"
      class="flex items-center gap-2 p-2 hover:bg-white/5 rounded-lg transition-colors"
    >
      <div class="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
        <span class="text-sm">{{ userInitials }}</span>
      </div>
      <ChevronDown 
        class="w-4 h-4 text-gray-400 transition-transform duration-200"
        :class="{ 'rotate-180': isProfileMenuOpen }"
      />
    </button>

    <!-- Profile Dropdown -->
    <div 
      v-if="isProfileMenuOpen"
      class="absolute right-0 mt-2 w-90 bg-gray-900 rounded-lg shadow-lg border border-white/10 py-2 animate-in fade-in slide-in-from-top-2 duration-200"
    >
      <!-- Email Address Tile -->
      <div class="px-4 py-3 border-b border-white/10">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
            <User class="w-5 h-5" />
          </div>
          <div>
            <div class="text-sm font-medium">{{ userEmail }}</div>
            <!-- <div class="text-xs text-gray-400">Free Plan</div> -->
          </div>
        </div>
      </div>
      
      <!-- Manage Subscriptions Tile -->
      <div class="py-1">
        <button 
          @click="handleSubscriptions"
          :disabled="isLoading"
          class="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-300 hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CreditCard class="w-4 h-4" />
          <span>{{ isLoading ? 'Loading...' : 'Manage Subscriptions' }}</span>
          <div v-if="isLoading" class="ml-auto">
            <div class="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </button>
      </div>
      
      <!-- Logout Tile -->
      <div class="border-t border-white/10 py-1">
        <button 
          @click="handleLogout"
          class="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-red-400 hover:bg-white/5 transition-colors"
        >
          <LogOut class="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useAuth } from '~/composables/useAuth'
import { 
  ChevronDown, 
  User, 
  CreditCard, 
  LogOut
} from 'lucide-vue-next'

const { fetchPortal } = usePortal()
const { logout, userEmail, subscriptionType } = useAuth()

// Props
defineProps({
  isProfileMenuOpen: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['toggle-profile-menu', 'close-profile-menu'])

// State
const isLoading = ref(false)

// Computed
const userInitials = computed(() => {
  if (!userEmail.value) return '?'
  return userEmail.value.charAt(0).toUpperCase()
})

// Methods
const toggleProfileMenu = () => {
  emit('toggle-profile-menu')
}

const handleSubscriptions = async () => {
  try {
    isLoading.value = true
    console.log(subscriptionType.value)
    if (subscriptionType.value == Subscription.BASIC) {
      navigateTo('/?section=pricing')
      return;
    }
    const portalUrl = await fetchPortal()
    if (portalUrl) {
      window.location.href = portalUrl
    } else {
      console.error('No portal URL returned:', portalUrl)
    }
  } catch (error) {
    console.error('Error fetching portal URL:', error)
  } finally {
    isLoading.value = false
  }
}

const handleLogout = async () => {
  emit('close-profile-menu')
  try {
    logout()
    navigateTo('/')
  } catch (err) {
    console.error('Logout error:', err)
  }
}
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-in-from-top-2 {
  from { transform: translateY(-8px); }
  to { transform: translateY(0); }
}

.animate-in {
  animation: fade-in 200ms ease-out, slide-in-from-top-2 200ms ease-out;
}</style>